import { useState, useEffect, useRef } from "react";
import { BookAppointmentModal } from "components/BookAppointment";
import DoctorCarousel from "components/DoctorCarousel";
import HeroReadMore from "components/HeroReadMore";
import scrollToSection from "utils/scrollToSection";
import mapsUrl from "utils/mapsUrl";
import visibleStats from "utils/visibleStats";
import AddressesModal from "components/site/AddressesModal";
import buildImgTransform from "utils/imgTransform";
import PhoneIcon from "components/site/PhoneIcon";
import { SpotlightCard, useParallax, Masonry, ChromaVeil, chromaTrack } from "components/site/fx";
import GalleryCarousel from "components/site/GalleryCarousel";
import { StarBorder } from "components/site/textfx";
import { TextType } from "components/site/textanim";
import SplashCursor from "components/site/SplashCursor";

// ── Default placeholder images ────────────────────────────────────────────────
const PLACEHOLDER_HERO = "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1600&q=80";
const PLACEHOLDER_GALLERY = [
  "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&q=80",
  "https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=800&q=80",
  "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=800&q=80",
  "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80",
];

const buildWhatsAppHref = (cfg) => {
  if (!cfg?.whatsapp_enabled || !cfg?.contact_phone) return null;
  const digits = String(cfg.contact_phone).replace(/\D/g, '');
  if (!digits) return null;
  const base = `https://wa.me/${digits}`;
  return cfg.whatsapp_message
    ? `${base}?text=${encodeURIComponent(cfg.whatsapp_message)}`
    : base;
};

// ── Icons ─────────────────────────────────────────────────────────────────────
const WhatsAppIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);
const IconMoon = () => <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>;
const IconSun = () => <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/></svg>;
const IconCheck = ({ size = 15 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const IconPhone = ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 014.69 12 19.79 19.79 0 011.61 3.38 2 2 0 013.6 1.2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L7.91 8.8a16 16 0 006 6l.86-.86a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0121.5 16v.92z"/></svg>;
const IconClock = ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const IconMapPin = ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const IconEmail = ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
const IconStar = ({ filled = true, size = 15, color = "#f59e0b" }) => <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : "none"} stroke={color} strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;

const ICON_MAP = {
  heart: ({ size, color }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>,
  person: ({ size, color }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  calendar: ({ size, color }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  shield: ({ size, color }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  globe: ({ size, color }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>,
  clock: ({ size, color }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
};
const getIcon = (name, size, color) => { const C = ICON_MAP[name] || ICON_MAP.shield; return <C size={size} color={color} />; };

// ── Condition enrichment — auto-tags from keywords ──────────────────────────
const COND_LIBRARY = [
  { kw: ['skin','eczema','psoriasis','acne','allerg','rash','dermat'], badge: 'Dermatology', period: '4–12 weeks', ages: 'All ages', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
  { kw: ['thyroid','diabetes','hormone','endocrin'], badge: 'Endocrine', period: 'Ongoing care', ages: 'Adults 20+', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
  { kw: ['asthma','sinusit','respirat','lung','breath','allerg'], badge: 'Respiratory', period: '6–8 weeks', ages: 'All ages', icon: 'M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z' },
  { kw: ['digest','gastric','stomach','ibs','bowel','liver','constip'], badge: 'Gastroenterology', period: '2–6 weeks', ages: 'All ages', icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z' },
  { kw: ['hair','scalp','alopecia'], badge: 'Trichology', period: '8–16 weeks', ages: 'Ages 15+', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
  { kw: ["women","female","gynec","menstr","pcod","pcos"], badge: 'Women\'s Health', period: 'Ongoing', ages: 'Women 12–55', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
  { kw: ['child','pediatr','infant','baby'], badge: 'Paediatrics', period: 'As needed', ages: 'Under 14', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
  { kw: ['stress','anxiety','sleep','mental','depress','insomnia'], badge: 'Mind & Body', period: '4–10 weeks', ages: 'Adults 18+', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
  { kw: ['joint','arthrit','rheumat','bone','ortho','back','pain','spine'], badge: 'Musculoskeletal', period: '6–12 weeks', ages: 'Adults 30+', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
];
function enrichCondition(str) {
  const s = str.toLowerCase();
  for (const lib of COND_LIBRARY) {
    if (lib.kw.some(k => s.includes(k))) return lib;
  }
  return { badge: 'General Care', period: 'Varies', ages: 'All ages', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' };
}

function Counter({ target }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        obs.disconnect();
        const end = parseInt(target, 10) || 0;
        if (end === 0) return;
        const step = Math.ceil(end / 50);
        let v = 0;
        const t = setInterval(() => { v = Math.min(v + step, end); setVal(v); if (v >= end) clearInterval(t); }, 25);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target]);
  return <span ref={ref}>{val.toLocaleString()}</span>;
}

function hexToRgb(hex) {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return r ? `${parseInt(r[1],16)},${parseInt(r[2],16)},${parseInt(r[3],16)}` : "79,140,255";
}

// ── Image Carousel ────────────────────────────────────────────────────────────
function ImageCarousel({ images, accent, title, T }) {
  if (!images || images.length === 0) return null;
  return (
    <section style={{ background: T.bgSection, padding: '72px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div data-reveal style={{ textAlign: 'center', marginBottom: 40 }}>
          <span style={{ display: 'inline-block', background: `rgba(${hexToRgb(accent)},0.1)`, color: accent, borderRadius: 100, padding: '4px 14px', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>Gallery</span>
          <h2 style={{ fontSize: 'clamp(22px, 2.8vw, 36px)', fontWeight: 800, color: T.text, letterSpacing: '-0.02em' }}>{title || 'Our Clinic'}</h2>
        </div>
        <GalleryCarousel images={images} accent={accent} />
      </div>
    </section>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function TemplateModern({ config, companyData }) {
  const [heroRef, heroParallax] = useParallax(0.18);
  const [modalOpen, setModalOpen] = useState(false);
  const [addrOpen, setAddrOpen] = useState(false);
  const [preselect, setPreselect] = useState({ doctorId: null, specId: null });
  const openBookWith = (doctorId, specId) => { setPreselect({ doctorId, specId }); setModalOpen(true); };
  const [dark, setDark] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredCond, setHoveredCond] = useState(null);
  const [hoveredWhy, setHoveredWhy] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [bioDoc, setBioDoc] = useState(null); // doctor card showing full bio (hover/tap)

  const accent = config.accentColor || "#2563eb";
  const accentRgb = hexToRgb(accent);
  const accentTint = `rgba(${accentRgb},0.08)`;

  const T = dark
    ? { bg: "#111318", bgCard: "#1c1f28", bgSection: "#161920", text: "#e8eaf0", textMid: "#9ba3b8", textMuted: "#606880", border: "rgba(255,255,255,0.06)", navBg: "rgba(17,19,24,0.97)" }
    : { bg: "#f7f8fc", bgCard: "#ffffff", bgSection: "#eef0f8", text: "#1a1d2e", textMid: "#4a5168", textMuted: "#8890a8", border: "rgba(0,0,0,0.07)", navBg: "rgba(247,248,252,0.97)" };

  const whatsappHref = buildWhatsAppHref(config);
  const heroBg = config.heroBannerImage || PLACEHOLDER_HERO;
  const galleryImages = (config.galleryImages || []).filter(Boolean);
  const carouselImages = (config.galleryCarousel || []).filter(Boolean);
  const imgAdjust = config.imageAdjust || {};

  const stats = config.stats || {};
  const statLabels = config.statLabels || {};
  const heroStats = visibleStats(config, { bookings: "Bookings", doctors: "Doctors", users: "Patients" });
  const bandStats = [
    ...visibleStats(config, { bookings: "Appointments", doctors: "Expert Doctors", users: "Happy Patients" }).map((s) => ({ ...s, suffix: "+" })),
    { key: "extra", val: null, label: statLabels.extra?.label || "Consultation Days", text: statLabels.extra?.value || "Mon–Sat" },
  ];
  const features = config.features || [];
  const testimonials = config.testimonials || [];
  const conditions = config.conditions || [];
  const whyChooseUs = config.whyChooseUs || [];
  const aboutBullets = config.aboutWhoWeAreBullets || [];
  const approachSteps = Array.isArray(config.aboutApproach) ? config.aboutApproach : [];
  const locations = config.locations || [];
  const doctorsList = (config.doctors || []).filter((d) => d.is_active && d.show_on_website !== false);
  const doctorsSection = config.doctorsSection || { enabled: true, title: 'Meet Our Doctors' };

  const scrollToId = (id) => scrollToSection(id);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    if (!els.length) return;
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.style.opacity = "1"; e.target.style.transform = "translateY(0) scale(1)"; e.target.style.filter = "blur(0)"; obs.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    els.forEach(el => {
      el.style.opacity = "0";
      el.style.transform = "translateY(32px) scale(0.98)";
      el.style.filter = "blur(5px)";
      el.style.transition = "opacity 0.7s ease, transform 0.7s cubic-bezier(.2,.7,.2,1), filter 0.7s ease";
      obs.observe(el);
    });
    return () => obs.disconnect();
  }, [dark]);

  // Menu order: About → Services (features) → Reviews → Contact.
  const navLinks = [
    { label: "About", href: "#about" },
    ...(features.length > 0 ? [{ label: "Services", href: "#services" }] : []),
    ...(testimonials.length > 0 ? [{ label: "Reviews", href: "#testimonials" }] : []),
    { label: "Contact", href: "#contact" },
  ];

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',system-ui,sans-serif", background: T.bg, color: T.text, minHeight: "100vh", overflowX: "hidden" }}>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        h1,h2,h3,p,span{overflow-wrap:break-word}
        /* Modern theme identity: editorial Instrument Serif headings */
        h1,h2{font-family:'Instrument Serif',Georgia,serif!important;font-weight:400!important;letter-spacing:-0.01em!important}
        html{scroll-behavior:smooth}
        section[id]{scroll-margin-top:72px}
        @media(max-width:768px){
          .tm-approach-grid{grid-template-columns:1fr!important;gap:24px!important}
          .tm-approach-line{display:none!important}
          .tm-footer-logo{display:none!important}
          .tm-about-media{flex:1 1 100%!important;max-width:100%!important;width:100%!important}
          .tm-cond-cloud{flex-direction:column!important;align-items:stretch!important}
          .tm-cond-pill{width:100%!important;min-height:62px!important}
          .tm-hero-inner{flex-direction:column!important}
          .tm-hero-left,.tm-hero-right{max-width:100%!important;flex:1 1 100%!important}
          .tm-hero-cta{flex-direction:column!important;align-items:stretch!important}
          .tm-hero-btn{width:100%!important;justify-content:center!important}
          .tm-feat-grid{grid-template-columns:1fr!important}
          .tm-test-grid{grid-template-columns:1fr!important}
          .tm-contact-grid{grid-template-columns:1fr!important}
          .tm-footer-grid{grid-template-columns:1fr 1fr!important}
          .tm-footer-cards{grid-template-columns:1fr!important}
          .tm-nav-links{display:none!important}
          .tm-mob-btn{display:flex!important}
          .tm-hide-mobile{display:none!important}
          .tm-why-row{padding:20px 16px!important}
          .tm-cond-grid{grid-template-columns:1fr!important}
        }
        @media(min-width:769px){.tm-mob-btn{display:none!important}.tm-mob-menu{display:none!important}}
      `}</style>

      {/* ── NAV ── */}
      <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, background: T.navBg, backdropFilter: "blur(12px)", borderBottom: `1px solid ${scrolled ? T.border : "transparent"}`, boxShadow: scrolled ? `0 2px 20px rgba(0,0,0,${dark ? "0.4" : "0.08"})` : "none", transition: "box-shadow 0.3s" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          {/* Brand — logo + name */}
          <a href="#" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", flexShrink: 0 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: accent, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
              {config.logo
                ? <img src={config.logo} alt={config.companyName} style={{ width: "100%", height: "100%", objectFit: "contain", transform: config.logoTransform }} />
                : <span style={{ color: "#fff", fontWeight: 800, fontSize: "1rem" }}>{config.companyName?.charAt(0) || "C"}</span>}
            </div>
            <span style={{ fontWeight: 700, fontSize: "1rem", color: T.text, letterSpacing: "-0.01em" }}>{config.companyName}</span>
          </a>

          {/* Center links */}
          <ul className="tm-nav-links" style={{ display: "flex", alignItems: "center", gap: 2, listStyle: "none", margin: 0, padding: 0 }}>
            {navLinks.map(l => (
              <li key={l.href}>
                <a href={l.href} onClick={e => { e.preventDefault(); scrollToId(l.href); }} style={{ padding: "7px 15px", borderRadius: 999, fontSize: "0.88rem", fontWeight: 500, color: T.textMid, cursor: "pointer", textDecoration: "none", transition: "color 0.25s, background 0.25s, transform 0.25s" }}
                  onMouseEnter={e => { e.target.style.color = "#fff"; e.target.style.background = accent; e.target.style.transform = "translateY(-1px)"; }}
                  onMouseLeave={e => { e.target.style.color = T.textMid; e.target.style.background = "transparent"; e.target.style.transform = "none"; }}>
                  {l.label}
                </a>
              </li>
            ))}
          </ul>

          {/* Right */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <button className="tm-hide-mobile" style={{ width: 34, height: 34, borderRadius: "50%", border: `1px solid ${T.border}`, background: T.bgCard, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T.textMid, flexShrink: 0 }} onClick={() => setDark(!dark)}>
              {dark ? <IconSun /> : <IconMoon />}
            </button>
            {whatsappHref && (
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="tm-hide-mobile"
                style={{ display: "flex", alignItems: "center", gap: 6, background: "#25d366", color: "#fff", borderRadius: 100, padding: "7px 14px", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none" }}>
                <WhatsAppIcon size={14} /> WhatsApp
              </a>
            )}
            <button className="tm-hide-mobile" onClick={() => setModalOpen(true)} style={{ background: accent, color: "#fff", border: "none", borderRadius: 100, padding: "8px 18px", fontWeight: 600, fontSize: "0.88rem", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
              Book Appointment
            </button>
            <button className="tm-mob-btn" onClick={() => setMobileMenu(!mobileMenu)} style={{ display: "none", background: "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 8px", cursor: "pointer", flexDirection: "column", gap: 4 }}>
              <span style={{ display: "block", width: 18, height: 2, background: T.text }} />
              <span style={{ display: "block", width: 18, height: 2, background: T.text }} />
              <span style={{ display: "block", width: 18, height: 2, background: T.text }} />
            </button>
          </div>
        </div>
        {mobileMenu && (
          <div className="tm-mob-menu" style={{ display: "flex", flexDirection: "column", padding: "16px 20px 22px", gap: 4, borderTop: `1px solid ${T.border}`, background: dark ? "rgba(17,19,24,0.78)" : "rgba(247,248,252,0.78)", backdropFilter: "blur(22px) saturate(180%)", WebkitBackdropFilter: "blur(22px) saturate(180%)", borderBottomLeftRadius: 18, borderBottomRightRadius: 18, boxShadow: "0 18px 40px rgba(0,0,0,0.18)" }}>
            {navLinks.map(l => (
              <a key={l.href} href={l.href} onClick={e => { e.preventDefault(); setMobileMenu(false); scrollToId(l.href); }} style={{ color: T.text, textDecoration: "none", padding: "10px 0", borderBottom: `1px solid ${T.border}`, fontSize: "0.95rem" }}>
                {l.label}
              </a>
            ))}
            {whatsappHref && (
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 8, background: "#25d366", color: "#fff", borderRadius: 8, padding: "10px 16px", marginTop: 12, textDecoration: "none", fontWeight: 600 }}>
                <WhatsAppIcon size={16} /> Chat on WhatsApp
              </a>
            )}
            <button onClick={() => { setModalOpen(true); setMobileMenu(false); }} style={{ marginTop: 10, background: accent, color: "#fff", border: "none", borderRadius: 50, padding: "12px", fontWeight: 700, cursor: "pointer" }}>
              Book Appointment
            </button>
          </div>
        )}
      </nav>

      {/* ── HERO — Split: text left, banner image right ── */}
      <section style={{ minHeight: "88vh", display: "flex", alignItems: "center", background: T.bg, paddingTop: 64 }}>
        <div className="tm-hero-inner" style={{ maxWidth: 1200, margin: "0 auto", padding: "clamp(40px,7vw,96px) 24px", display: "flex", alignItems: "center", gap: 40, width: "100%" }}>
          {/* Left text */}
          <div className="tm-hero-left" style={{ flex: "0 0 52%", maxWidth: "52%" }} data-reveal>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: accentTint, color: accent, borderRadius: 100, padding: "5px 14px", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 18 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              Trusted Healthcare
            </div>
            <h1 style={{ fontSize: "clamp(28px, 4vw, 52px)", fontWeight: 800, color: T.text, lineHeight: 1.12, letterSpacing: "-0.03em", margin: "0 0 14px" }}>
              <TextType text={config.companyName || ''} typingSpeed={80} loop={false} cursorChar="_" style={{ color: T.text }} />
            </h1>
            {config.tagline && (
              <p style={{ fontSize: "clamp(16px, 1.8vw, 22px)", color: accent, fontStyle: "italic", fontWeight: 500, marginBottom: 16 }}>{config.tagline}</p>
            )}
            {config.heroSubtext && (
              <p style={{ fontSize: "1.02rem", color: T.textMid, lineHeight: 1.7, marginBottom: 10 }}>{config.heroSubtext}</p>
            )}
            <HeroReadMore paragraphs={config.heroExtraParagraphs} heading={config.companyName} linkColor={accent} />

            <div className="tm-hero-cta" style={{ display: "flex", gap: 12, marginTop: 28, flexWrap: "wrap" }}>
              <StarBorder color={accent} style={{ display: "block" }}>
                <button className="tm-hero-btn" onClick={() => setModalOpen(true)} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", background: "transparent", color: accent, border: "none", borderRadius: 100, padding: "13px 28px", fontWeight: 700, fontSize: "0.97rem", cursor: "pointer" }}>
                  Book Appointment
                </button>
              </StarBorder>
              {whatsappHref && (
                <a className="tm-hero-btn" href={whatsappHref} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "#25d366", color: "#fff", borderRadius: 100, padding: "13px 24px", fontWeight: 700, fontSize: "0.95rem", textDecoration: "none", boxShadow: "0 4px 16px rgba(37,211,102,0.3)" }}>
                  <WhatsAppIcon size={18} /> WhatsApp
                </a>
              )}
            </div>
          </div>

          {/* Right — banner image with stats overlay */}
          <div className="tm-hero-right" style={{ flex: "0 0 48%", maxWidth: "48%" }} data-reveal>
            <div ref={heroRef} style={{ position: "relative", borderRadius: 24, overflow: "hidden", boxShadow: dark ? "0 24px 64px rgba(0,0,0,0.5)" : "0 24px 64px rgba(0,0,0,0.12)" }}>
              <img src={heroBg} alt={config.companyName} style={{ width: "100%", height: "clamp(280px,36vw,420px)", objectFit: "cover", display: "block", transform: `${heroParallax} scale(1.15)`, transition: "transform 0.1s linear" }} onError={e => { e.target.src = PLACEHOLDER_HERO; }} />
              {/* Dark overlay gradient */}
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.1) 50%)" }} />
              {/* Stats bar at bottom */}
              {heroStats.length > 0 && (
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "20px 24px", display: "flex", justifyContent: "space-around", backdropFilter: "blur(4px)", background: "rgba(0,0,0,0.25)" }}>
                {heroStats.map((s) => (
                  <div key={s.key} style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#fff", lineHeight: 1 }}>{s.val}+</div>
                    <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)", marginTop: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}>{s.label}</div>
                  </div>
                ))}
              </div>
              )}
            </div>
            {/* Quick Book button below image */}
            <button onClick={() => setModalOpen(true)} style={{ width: "100%", marginTop: 16, background: accent, color: "#fff", border: "none", borderRadius: 14, padding: "14px 0", fontWeight: 700, fontSize: "1rem", cursor: "pointer", boxShadow: `0 4px 16px rgba(${accentRgb},0.3)`, transition: "opacity 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
              Quick Book Appointment →
            </button>
          </div>
        </div>
      </section>

      {/* ── STATS BAND ── */}
      <div style={{ background: accent, padding: "0 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 0 }}>
          {bandStats.map((s, i) => (
            <div key={s.key || i} style={{ flex: "1 1 160px", textAlign: "center", padding: "28px 20px", borderRight: i < bandStats.length - 1 ? "1px solid rgba(255,255,255,0.2)" : "none" }}>
              <div style={{ fontSize: "clamp(26px, 3vw, 40px)", fontWeight: 800, color: "#fff", lineHeight: 1, letterSpacing: "-0.02em" }}>
                {s.val != null ? <><Counter target={s.val} />{s.suffix}</> : s.text}
              </div>
              <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.75)", marginTop: 6, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FEATURES — dark glass cards ── */}
      {features.length > 0 && (
        <section id="services" style={{ background: dark ? "#08090f" : "#0d1224", padding: "80px 24px" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 52 }} data-reveal>
              <span style={{ display: "inline-block", background: `rgba(${accentRgb},0.18)`, color: accent, borderRadius: 100, padding: "4px 14px", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>Services</span>
              <h2 style={{ fontSize: "clamp(22px, 2.8vw, 38px)", fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" }}>What We Offer</h2>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 22, justifyContent: "center" }}>
              {features.map((f, i) => (
                <SpotlightCard key={i} glow={accent} radius={20} borderColor="rgba(255,255,255,0.1)"
                  style={{ flex: "1 1 240px", maxWidth: 320, background: "rgba(255,255,255,0.05)", backdropFilter: "blur(10px)" }}>
                  <div data-reveal style={{ padding: "36px 28px" }}>
                    <div style={{ width: 58, height: 58, borderRadius: 16, background: `rgba(${accentRgb},0.2)`, border: `1px solid rgba(${accentRgb},0.3)`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                      {getIcon(f.icon, 26, "#fff")}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: "1.08rem", color: "#fff", marginBottom: 8, letterSpacing: "-0.01em" }}>{f.title}</div>
                    <div style={{ fontSize: "0.92rem", color: "rgba(255,255,255,0.6)", lineHeight: 1.72 }}>{f.desc}</div>
                  </div>
                </SpotlightCard>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── WHY CHOOSE US ── */}
      {whyChooseUs.length > 0 && (
        <section style={{ background: T.bg, padding: "80px 24px" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 52 }} data-reveal>
              <span style={{ display: "inline-block", background: accentTint, color: accent, borderRadius: 100, padding: "4px 14px", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>Why Us</span>
              <h2 style={{ fontSize: "clamp(22px, 2.8vw, 38px)", fontWeight: 800, color: T.text, letterSpacing: "-0.02em" }}>
                Why Patients Choose <span style={{ color: accent }}>{config.companyName}</span>
              </h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 22 }}>
              {whyChooseUs.map((point, i) => (
                <div key={i} data-reveal
                  onMouseEnter={() => setHoveredWhy(i)}
                  onMouseLeave={() => setHoveredWhy(null)}
                  style={{
                    position: "relative", overflow: "hidden",
                    background: hoveredWhy === i ? accent : T.bgCard,
                    borderRadius: 20, padding: "30px 26px",
                    border: `1.5px solid ${hoveredWhy === i ? accent : T.border}`,
                    cursor: "default",
                    transition: "all 0.25s ease",
                    transform: hoveredWhy === i ? "translateY(-7px) scale(1.02)" : "none",
                    boxShadow: hoveredWhy === i ? `0 20px 48px rgba(${accentRgb},0.28)` : "0 2px 10px rgba(0,0,0,0.04)",
                  }}>
                  {/* Top gradient line */}
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${accent}, rgba(${accentRgb},0.4))`, borderRadius: "20px 20px 0 0", opacity: hoveredWhy === i ? 0 : 1, transition: "opacity 0.25s" }} />
                  {/* Watermark */}
                  <div style={{ position: "absolute", right: 14, top: 8, fontSize: "4.5rem", fontWeight: 900, color: hoveredWhy === i ? "rgba(255,255,255,0.08)" : `rgba(${accentRgb},0.06)`, lineHeight: 1, userSelect: "none", pointerEvents: "none", transition: "color 0.25s" }}>
                    {String(i+1).padStart(2,"0")}
                  </div>
                  {/* Icon circle */}
                  <div style={{ width: 50, height: 50, borderRadius: "50%", background: hoveredWhy === i ? "rgba(255,255,255,0.18)" : accentTint, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, transition: "background 0.25s", boxShadow: hoveredWhy === i ? "none" : `0 4px 14px rgba(${accentRgb},0.2)` }}>
                    <IconCheck size={20} color={hoveredWhy === i ? "#fff" : accent} />
                  </div>
                  <p style={{ fontSize: "0.96rem", color: hoveredWhy === i ? "rgba(255,255,255,0.92)" : T.textMid, fontWeight: 500, lineHeight: 1.68, margin: 0, transition: "color 0.25s" }}>{point}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CONDITIONS — dark pill cloud ── */}
      {conditions.length > 0 && (
        <section style={{ background: dark ? "#08090f" : "#0d1224", padding: "80px 24px" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 52 }} data-reveal>
              <span style={{ display: "inline-block", background: `rgba(${accentRgb},0.18)`, color: accent, borderRadius: 100, padding: "4px 14px", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 14 }}>We Treat</span>
              <h2 style={{ fontSize: "clamp(22px, 2.8vw, 38px)", fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", marginBottom: 10 }}>Conditions We Specialise In</h2>
              {config.conditionsTagline && <p style={{ fontSize: "1rem", color: "rgba(255,255,255,0.55)", lineHeight: 1.65, maxWidth: 540, margin: "0 auto" }}>{config.conditionsTagline}</p>}
            </div>
            {/* Pill cloud */}
            <div className="tm-cond-cloud" style={{ display: "flex", flexWrap: "wrap", gap: 14, justifyContent: "center" }} data-reveal>
              {conditions.map((c, i) => {
                const meta = enrichCondition(c);
                const hov = hoveredCond === i;
                return (
                  <div key={i} className="tm-cond-pill"
                    onMouseEnter={() => setHoveredCond(i)}
                    onMouseLeave={() => setHoveredCond(null)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      background: hov ? accent : "rgba(255,255,255,0.07)",
                      border: `1px solid ${hov ? accent : "rgba(255,255,255,0.12)"}`,
                      borderRadius: 50, padding: "9px 20px 9px 10px",
                      cursor: "default",
                      transition: "all 0.22s ease",
                      transform: hov ? "translateY(-3px) scale(1.05)" : "none",
                      boxShadow: hov ? `0 8px 28px rgba(${accentRgb},0.4)` : "none",
                    }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: hov ? "rgba(255,255,255,0.2)" : `rgba(${accentRgb},0.2)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={hov ? "#fff" : accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={meta.icon}/></svg>
                    </div>
                    <div>
                      <div style={{ fontSize: "0.88rem", fontWeight: 600, color: hov ? "#fff" : "rgba(255,255,255,0.88)", lineHeight: 1.2 }}>{c}</div>
                      <div style={{ fontSize: "0.68rem", color: hov ? "rgba(255,255,255,0.72)" : "rgba(255,255,255,0.4)", marginTop: 2 }}>{meta.badge}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── ABOUT ── */}
      <section id="about" style={{ background: dark ? "#08090f" : "#0d1224", padding: "80px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          {/* Section header */}
          <div style={{ textAlign: "center", marginBottom: 56 }} data-reveal>
            <span style={{ display: "inline-block", background: `rgba(${accentRgb},0.15)`, color: accent, borderRadius: 100, padding: "4px 14px", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>About Us</span>
            <h2 style={{ fontSize: "clamp(22px, 2.8vw, 38px)", fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" }}>{config.companyName}</h2>
          </div>

          {/* Main: image left + text right */}
          <div style={{ display: "flex", gap: 48, alignItems: "flex-start", flexWrap: "wrap" }}>
            {/* Left: single cinematic image */}
            <div data-reveal className="tm-about-media" style={{ flex: "0 0 44%", minWidth: 280 }}>
              <Masonry images={(galleryImages.length > 0 ? galleryImages : PLACEHOLDER_GALLERY).slice(0, 5)} columns={2} gap={10} radius={18} accent={accent} onImageClick={(src) => setLightbox(src)} />
            </div>

            {/* Right: text on dark bg */}
            <div data-reveal style={{ flex: "1 1 300px", minWidth: 260 }}>
              {config.aboutWhoWeAre && (
                <p style={{ fontSize: "1.05rem", color: "rgba(255,255,255,0.72)", lineHeight: 1.78, marginBottom: 24 }}>{config.aboutWhoWeAre}</p>
              )}
              {aboutBullets.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 22 }}>
                  {aboutBullets.map((b, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 16px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, fontSize: "0.93rem", color: "rgba(255,255,255,0.72)" }}>
                      <div style={{ width: 24, height: 24, borderRadius: "50%", background: accent, color: "#fff", fontWeight: 800, fontSize: "0.7rem", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                      {b}
                    </div>
                  ))}
                </div>
              )}
              {config.aboutMission && (
                <div style={{ background: `rgba(${accentRgb},0.1)`, border: `1px solid rgba(${accentRgb},0.28)`, borderRadius: 16, padding: "20px 22px", marginBottom: 22 }}>
                  <div style={{ fontSize: "0.72rem", fontWeight: 700, color: accent, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Our Mission</div>
                  <p style={{ fontSize: "0.97rem", color: "rgba(255,255,255,0.8)", fontStyle: "italic", lineHeight: 1.72, margin: 0 }}>{config.aboutMission}</p>
                </div>
              )}
              <button onClick={() => setModalOpen(true)} style={{ background: accent, color: "#fff", border: "none", borderRadius: 100, padding: "12px 28px", fontWeight: 700, fontSize: "0.95rem", cursor: "pointer", boxShadow: `0 4px 16px rgba(${accentRgb},0.4)`, transition: "opacity 0.2s, transform 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "none"; }}>
                Book Appointment →
              </button>
            </div>
          </div>

          {/* Our Approach — horizontal steps */}
          {approachSteps.length > 0 && (
            <div style={{ marginTop: 72 }} data-reveal>
              <div style={{ textAlign: "center", marginBottom: 44 }}>
                <span style={{ display: "inline-block", background: `rgba(${accentRgb},0.15)`, color: accent, borderRadius: 100, padding: "4px 14px", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>Methodology</span>
                <h3 style={{ fontSize: "clamp(20px, 2.5vw, 32px)", fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" }}>Our Approach</h3>
              </div>
              <div className="tm-approach-grid" style={{ display: "grid", gridTemplateColumns: `repeat(${approachSteps.length}, minmax(0,1fr))`, gap: 0, position: "relative" }}>
                <div className="tm-approach-line" style={{ position: "absolute", top: 28, left: "8%", right: "8%", height: 2, background: `linear-gradient(90deg, transparent, ${accent}50, ${accent}50, transparent)`, zIndex: 0, display: approachSteps.length > 1 ? "block" : "none" }} />
                {approachSteps.map((step, i) => (
                  <div key={i} style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "0 16px 0" }}>
                    <div style={{ width: 56, height: 56, borderRadius: "50%", background: accent, color: "#fff", fontWeight: 800, fontSize: "1.1rem", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, boxShadow: `0 6px 20px rgba(${accentRgb},0.45)`, border: `3px solid ${dark ? "#08090f" : "#0d1224"}` }}>{i + 1}</div>
                    <p style={{ fontSize: "0.91rem", color: "rgba(255,255,255,0.68)", lineHeight: 1.65, margin: 0 }}>{step}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── GALLERY CAROUSEL ── */}
      {carouselImages.length > 0 && <ImageCarousel images={carouselImages} accent={accent} title={config.galleryTitle} T={T} />}

      {/* ── MEET OUR DOCTORS ── */}
      {doctorsSection.enabled && doctorsList.length > 0 && (
        <section style={{ padding: "clamp(60px,9vw,110px) 24px", background: T.bgSection, position: "relative" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 7 }}>
            <div style={{ textAlign: "center", marginBottom: 50 }} data-reveal>
              <span style={{ display: "inline-block", background: accentTint, color: accent, borderRadius: 100, padding: "4px 14px", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>Our Team</span>
              <h2 style={{ fontSize: "clamp(24px, 3vw, 38px)", fontWeight: 800, color: T.text, letterSpacing: "-0.02em" }}>{doctorsSection.title || 'Meet Our Doctors'}</h2>
            </div>
            <DoctorCarousel accent={accent}>
              {doctorsList.map((d, i) => (
                <div key={d.id} style={{ position: "relative", background: T.bgCard, borderRadius: 20, overflow: "hidden", border: `1px solid ${T.border}`, display: "flex", flexDirection: "column" }}
                  >
                  <div style={{ aspectRatio: "4 / 3", background: `linear-gradient(135deg, ${accentTint}, ${T.bgSection})`, position: "relative", overflow: "hidden" }}>
                    {d.photo ? (
                      <img src={d.photo} alt={d.name} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transform: buildImgTransform({ scale: d.photo_scale, position: d.photo_position }), transformOrigin: "center" }} />
                    ) : (
                      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "4rem", fontWeight: 800, color: accent, opacity: 0.4 }}>
                        {d.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div style={{ padding: "20px 22px 22px", display: "flex", flexDirection: "column", flex: 1 }}>
                    <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: T.text, marginBottom: 4 }}>{d.name}</h3>
                    {d.qualifications && <div style={{ fontSize: "0.78rem", color: T.textMuted, marginBottom: 8 }}>{d.qualifications}</div>}
                    {d.specialization_name && (
                      <div style={{ display: "inline-block", alignSelf: "flex-start", fontSize: "0.7rem", fontWeight: 700, color: accent, background: accentTint, padding: "3px 10px", borderRadius: 20, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                        {d.specialization_name}
                      </div>
                    )}
                    {d.experience_years > 0 && (
                      <div style={{ fontSize: "0.85rem", color: T.textMid, marginBottom: 8 }}>
                        <span style={{ color: accent, fontWeight: 700 }}>{d.experience_years}+</span> {d.experience_years === 1 ? "year" : "years"} of experience
                      </div>
                    )}
                    {d.bio && <p style={{ fontSize: "0.85rem", color: T.textMid, lineHeight: 1.6, marginBottom: 6, display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{d.bio}</p>}
                    {d.bio && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setBioDoc((cur) => (cur === d.id ? null : d.id)); }}
                        style={{ alignSelf: "flex-end", background: "none", border: "none", color: accent, fontWeight: 700, fontSize: "0.8rem", cursor: "pointer", padding: 0, marginBottom: 18 }}>
                        View full bio →
                      </button>
                    )}
                    <button onClick={() => openBookWith(d.id, d.specialization)}
                      style={{ marginTop: "auto", background: accent, color: "#fff", border: "none", borderRadius: 100, padding: "10px 18px", fontSize: "0.85rem", fontWeight: 700, cursor: "pointer" }}>
                      Book Appointment →
                    </button>
                  </div>

                  {/* Full-bio reveal — covers the card on hover/tap, reverts on mouse-leave */}
                  {d.bio && (
                    <div style={{ position: "absolute", inset: 0, background: T.bgCard, padding: 22, display: "flex", flexDirection: "column", gap: 8, opacity: bioDoc === d.id ? 1 : 0, pointerEvents: bioDoc === d.id ? "auto" : "none", transition: "opacity 0.25s", overflowY: "auto" }}>
                      <button type="button" onClick={() => setBioDoc(null)}
                        style={{ alignSelf: "flex-start", background: "none", border: "none", color: accent, fontWeight: 700, fontSize: "0.8rem", cursor: "pointer", padding: 0, marginBottom: 2 }}>
                        ← Back
                      </button>
                      <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: T.text, margin: 0 }}>{d.name}</h3>
                      {d.specialization_name && <div style={{ fontSize: "0.7rem", fontWeight: 700, color: accent, textTransform: "uppercase", letterSpacing: "0.06em" }}>{d.specialization_name}</div>}
                      <p style={{ fontSize: "0.84rem", color: T.textMid, lineHeight: 1.65, margin: "4px 0 0", whiteSpace: "pre-line" }}>{d.bio}</p>
                      <button onClick={() => openBookWith(d.id, d.specialization)}
                        style={{ marginTop: "auto", background: accent, color: "#fff", border: "none", borderRadius: 100, padding: "10px 18px", fontSize: "0.85rem", fontWeight: 700, cursor: "pointer" }}>
                        Book Appointment →
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </DoctorCarousel>
          </div>
        </section>
      )}

      {/* ── TESTIMONIALS — 2 col with avatar ── */}
      {testimonials.length > 0 && (
        <section id="testimonials" style={{ background: T.bgSection, padding: "72px 24px" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 44 }} data-reveal>
              <span style={{ display: "inline-block", background: accentTint, color: accent, borderRadius: 100, padding: "4px 14px", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>Reviews</span>
              <h2 style={{ fontSize: "clamp(22px, 2.8vw, 36px)", fontWeight: 800, color: T.text, letterSpacing: "-0.02em" }}>What Our Patients Say</h2>
            </div>
            <div className="tm-test-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 22 }}>
              {testimonials.map((t, i) => {
                const first = t.user__first_name || "";
                const last = t.user__last_name || "";
                const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "P";
                const name = [first, last].filter(Boolean).join(" ") || "Patient";
                const stars = typeof t.rating === "number" ? t.rating : 5;
                return (
                  <div key={t.id || i} data-reveal style={{ background: T.bgCard, borderRadius: 16, padding: "24px", border: `1px solid ${T.border}`, display: "flex", gap: 18, alignItems: "flex-start" }}>
                    <div style={{ width: 56, height: 56, borderRadius: "50%", background: accent, color: "#fff", fontWeight: 700, fontSize: "1.3rem", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{initials}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: "0.93rem", color: T.textMid, fontStyle: "italic", lineHeight: 1.65, marginBottom: 10 }}>"{t.feedback}"</p>
                      <div style={{ display: "flex", gap: 2, marginBottom: 6 }}>
                        {Array.from({ length: 5 }).map((_, si) => <IconStar key={si} filled={si < stars} size={13} />)}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: "0.92rem", color: T.text }}>{name}</div>
                      <div style={{ fontSize: "0.76rem", color: T.textMuted, letterSpacing: "0.02em" }}>Verified Patient</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── CONTACT / CTA ── */}
      <section id="contact" style={{ background: T.bgCard, padding: "72px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ marginBottom: 40 }} data-reveal>
            <span style={{ display: "inline-block", background: accentTint, color: accent, borderRadius: 100, padding: "4px 14px", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>Get In Touch</span>
            <h2 style={{ fontSize: "clamp(22px, 2.8vw, 36px)", fontWeight: 800, color: T.text, letterSpacing: "-0.02em" }}>Contact & Booking</h2>
            {config.contactIntro && <p style={{ fontSize: "1.02rem", color: T.textMid, lineHeight: 1.65, marginTop: 8, maxWidth: 560 }}>{config.contactIntro}</p>}
          </div>
          <div className="tm-contact-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28 }}>
            {/* Info cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }} data-reveal>
              {[
                { icon: <IconPhone />, title: "Phone & Email", value: [config.contact_phone, config.contact_email].filter(Boolean).join(" · ") || "Book via appointment form" },
                ...(locations.length > 0 ? [{
                  icon: <IconMapPin />, title: locations.length === 1 ? "Location" : `Locations (${locations.length})`,
                  value: (
                    <div>
                      {locations.map((l, idx) => {
                        const isObj = typeof l === "object" && l !== null;
                        const nm = isObj ? (l.name || `Branch ${idx + 1}`) : l;
                        return (
                          <a key={isObj && l.id ? l.id : idx} href={mapsUrl(l)} target="_blank" rel="noopener noreferrer" title={isObj ? l.address : nm}
                            style={{ display: "flex", alignItems: "center", gap: 6, color: accent, fontWeight: 600, fontSize: "0.9rem", textDecoration: "none", marginBottom: 6 }}>
                            <IconMapPin size={13} /> {nm}
                          </a>
                        );
                      })}
                      <button type="button" onClick={() => setAddrOpen(true)}
                        style={{ background: "none", border: "none", padding: 0, marginTop: 2, cursor: "pointer", color: T.textMid, fontSize: "0.82rem", fontWeight: 600, textDecoration: "none" }}>
                        View full address{locations.length > 1 ? "es" : ""} →
                      </button>
                    </div>
                  ),
                }] : []),
                ...(whatsappHref ? [{ icon: <WhatsAppIcon size={18} />, title: "WhatsApp", value: "Chat with us directly", href: whatsappHref, green: true }] : []),
              ].map((card, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 16, background: card.green ? "#25d36610" : T.bgSection, borderRadius: 14, padding: "18px 20px", border: `1px solid ${card.green ? "#25d36640" : T.border}` }}>
                  <div style={{ width: 42, height: 42, borderRadius: "50%", background: card.green ? "#25d366" : accentTint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: card.green ? "#fff" : accent }}>{card.icon}</div>
                  <div>
                    <div style={{ fontSize: "0.8rem", fontWeight: 700, color: T.text, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{card.title}</div>
                    {card.href
                      ? <a href={card.href} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.92rem", color: "#25d366", textDecoration: "none" }}>{card.value}</a>
                      : <div style={{ fontSize: "0.92rem", color: T.textMid, lineHeight: 1.5 }}>{card.value}</div>}
                  </div>
                </div>
              ))}
            </div>
            {/* CTA panel */}
            <div style={{ background: accent, borderRadius: 20, padding: "36px", textAlign: "center" }} data-reveal>
              <h3 style={{ fontSize: "clamp(20px, 2.2vw, 30px)", fontWeight: 800, color: "#fff", marginBottom: 12, letterSpacing: "-0.02em" }}>Book Your Consultation</h3>
              <p style={{ fontSize: "0.98rem", color: "rgba(255,255,255,0.85)", lineHeight: 1.65, marginBottom: 24 }}>
                {config.closingCta || "We're here to guide you with care, clarity, and confidence."}
              </p>
              <button onClick={() => setModalOpen(true)} style={{ background: "#fff", color: accent, border: "none", borderRadius: 100, padding: "14px 32px", fontWeight: 700, fontSize: "1rem", cursor: "pointer", boxShadow: "0 4px 18px rgba(0,0,0,0.12)", transition: "opacity 0.2s, transform 0.15s", marginBottom: whatsappHref ? 12 : 0 }}
                onMouseEnter={e => { e.currentTarget.style.opacity = "0.9"; e.currentTarget.style.transform = "scale(0.98)"; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "scale(1)"; }}>
                Book Appointment
              </button>
              {whatsappHref && (
                <div>
                  <a href={whatsappHref} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#25d366", color: "#fff", borderRadius: 100, padding: "12px 28px", fontWeight: 700, fontSize: "0.95rem", textDecoration: "none" }}>
                    <WhatsAppIcon size={17} /> Chat on WhatsApp
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── MAIN FOOTER ── */}
      <footer style={{ background: "#1a1d2e", padding: "48px 24px 0", position: "relative", overflow: "hidden" }}>
        <SplashCursor colors={[accent, "#22d3ee", "#ffffff"]} />
        <div style={{ position: "relative", zIndex: 1 }}>
        <div className="tm-footer-grid" style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1.5fr", gap: 36, paddingBottom: 44, borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div className="tm-footer-logo" style={{ width: 36, height: 36, borderRadius: 10, background: accent, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                {config.logo ? <img src={config.logo} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", transform: config.logoTransform }} /> : <span style={{ color: "#fff", fontWeight: 800 }}>{config.companyName?.charAt(0)}</span>}
              </div>
              <span style={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}>{config.companyName}</span>
            </div>
            <p style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.45)", lineHeight: 1.65 }}>
              {config.footerTagline || `${config.companyName} — Your trusted healthcare partner`}
            </p>
            {whatsappHref && (
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 14, background: "#25d366", color: "#fff", borderRadius: 50, padding: "7px 14px", fontSize: "0.8rem", fontWeight: 600, textDecoration: "none" }}>
                <WhatsAppIcon size={13} /> WhatsApp Us
              </a>
            )}
          </div>
          <div>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Links</div>
            {navLinks.map(l => <a key={l.href} href={l.href} onClick={e => { e.preventDefault(); scrollToId(l.href); }} style={{ display: "block", fontSize: "0.87rem", color: "rgba(255,255,255,0.6)", marginBottom: 8, textDecoration: "none" }} onMouseEnter={e => e.target.style.color = "#fff"} onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.6)"}>{l.label}</a>)}
          </div>
          <div>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Specialties</div>
            {conditions.slice(0, 5).map((c, i) => <div key={i} style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.55)", marginBottom: 6 }}>{c.length > 30 ? c.substring(0, 30) + "…" : c}</div>)}
          </div>
          <div>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Contact</div>
            {config.contact_phone && <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: "0.87rem", color: "rgba(255,255,255,0.6)" }}><IconPhone size={14} />{config.contact_phone}</div>}
            {config.contact_email && <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: "0.87rem", color: "rgba(255,255,255,0.6)" }}><IconEmail size={14} />{config.contact_email}</div>}
            {locations.map((loc, i) => {
              const isObj = typeof loc === "object" && loc !== null;
              const name = isObj ? (loc.name || `Branch ${i + 1}`) : loc;
              const addr = isObj ? loc.address : "";
              const ph = isObj ? loc.phone : "";
              return (
                <div key={isObj && loc.id ? loc.id : i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 12, fontSize: "0.87rem", color: "rgba(255,255,255,0.6)" }}>
                  <IconMapPin size={14} />
                  <div style={{ flex: 1 }}>
                    <a href={mapsUrl(loc)} target="_blank" rel="noopener noreferrer" title={addr || name} style={{ fontWeight: 600, color: "rgba(255,255,255,0.8)", textDecoration: "none" }}>{name}</a>
                    {ph && <a href={`tel:${ph}`} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, color: "rgba(255,255,255,0.55)", fontSize: "0.78rem", textDecoration: "none" }}><PhoneIcon size={12} /> {ph}</a>}
                  </div>
                </div>
              );
            })}
            {locations.length > 0 && (
              <button type="button" onClick={() => setAddrOpen(true)} style={{ background: "none", border: "none", padding: 0, marginBottom: 4, cursor: "pointer", color: accent, fontSize: "0.8rem", fontWeight: 600, textDecoration: "none" }}>
                ⓘ View full address{locations.length > 1 ? "es" : ""}
              </button>
            )}
            <button onClick={() => setModalOpen(true)} style={{ marginTop: 12, display: "block", width: "100%", textAlign: "center", background: accent, color: "#fff", border: "none", borderRadius: 50, padding: "9px 0", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
              Book Appointment
            </button>
          </div>
        </div>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "18px 0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <span style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.3)" }}>© {new Date().getFullYear()} {config.companyName}. All rights reserved.</span>
          <span style={{ fontSize: "0.75rem", color: "#ffffff", display: "flex", alignItems: "center", gap: 5 }}><span style={{ color: "#ef4444" }}>♥</span> Powered by ZoidesQ</span>
        </div>
        </div>
      </footer>

      {/* ── Floating WhatsApp ── */}
      {whatsappHref && (
        <a href={whatsappHref} target="_blank" rel="noopener noreferrer" title="Chat on WhatsApp"
          style={{ position: "fixed", bottom: 24, left: 24, zIndex: 200, width: 52, height: 52, borderRadius: "50%", background: "#25d366", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(37,211,102,0.45)", textDecoration: "none", transition: "transform 0.2s, box-shadow 0.2s" }}
          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(37,211,102,0.55)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(37,211,102,0.45)"; }}>
          <WhatsAppIcon size={26} />
        </a>
      )}
      {/* Dark mode toggle */}
      <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 200 }}>
        <button onClick={() => setDark(!dark)} style={{ width: 44, height: 44, borderRadius: "50%", background: dark ? "#e6edf3" : "#1a1a1a", color: dark ? "#0d1117" : "#fff", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}>
          {dark ? <IconSun /> : <IconMoon />}
        </button>
      </div>

      <BookAppointmentModal
        id={companyData?.company_id}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setPreselect({ doctorId: null, specId: null }); }}
        preselectedDoctorId={preselect.doctorId}
        preselectedSpecId={preselect.specId}
        onSuccess={() => {}}
        accentColor={accent}
      />
      <AddressesModal open={addrOpen} onClose={() => setAddrOpen(false)} locations={locations} accent={accent} />

      {/* Image lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(0,0,0,0.9)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, cursor: "zoom-out" }}>
          <img src={lightbox} alt="" style={{ maxWidth: "94vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }} />
          <button onClick={() => setLightbox(null)} aria-label="Close"
            style={{ position: "fixed", top: 20, right: 24, width: 44, height: 44, borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.15)", color: "#fff", fontSize: "1.6rem", cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
      )}
    </div>
  );
}
